const ROOT_VARS = [
    '--primary-cyan',
    '--accent-magenta',
    '--ui-link',
    '--ui-info',
    '--ui-success',
    '--ui-warning',
    '--ui-danger',
    '--bg-deep',
    '--bg-surface',
    '--bg-surface-alt',
    '--text-main',
    '--text-muted'
];

const SECTION_TITLE_SELECTORS = [
    '.ui-title.size-2',
    '.ui-heading',
    '.ui-surface-title',
    '.ui-hero-title'
];

function safeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
}

function selectorTextList(root, selector, limit = 8) {
    return Array.from(root.querySelectorAll(selector))
        .map((node) => safeText(node.textContent))
        .filter(Boolean)
        .slice(0, limit);
}

function rectSnapshot(element) {
    if (!element) {
        return null;
    }
    const rect = element.getBoundingClientRect();
    return {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        bottom: Math.round(rect.bottom)
    };
}

function styleSnapshot(element, props) {
    if (!element) {
        return null;
    }
    const computed = window.getComputedStyle(element);
    return Object.fromEntries(props.map((prop) => [prop, computed.getPropertyValue(prop).trim()]));
}

function collectRootVars() {
    const computed = window.getComputedStyle(document.documentElement);
    return Object.fromEntries(ROOT_VARS.map((name) => [name, computed.getPropertyValue(name).trim()]));
}

function collectShell() {
    const navbar = document.querySelector('.ui-navbar, nav.navbar, .navbar');
    const navbarBrand = document.querySelector('.ui-navbar-brand, .navbar-brand');
    const navbarMenu = document.querySelector('.ui-navbar-menu, .navbar-menu');
    const navbarEnd = document.querySelector('.ui-navbar-end, .navbar-end');
    const sidebar = document.querySelector('.ui-menu, .menu');
    const sidebarStickyHost = document.querySelector('body.arcate-full > .ui-layout.gap-0 > .ui-sidebar, body.arcate-full > .ui-layout.gap-0 > :first-child');
    const shell = document.querySelector('body.arcate-full > .ui-layout.gap-0');
    const main = document.querySelector('main[role="main"]');
    const hero = main?.querySelector(':scope > .ui-hero') || null;

    const navbarRect = rectSnapshot(navbar);
    const brandRect = rectSnapshot(navbarBrand);
    const menuRect = rectSnapshot(navbarMenu);
    const endRect = rectSnapshot(navbarEnd);
    const sidebarRect = rectSnapshot(sidebar);
    const childTops = [brandRect?.top, menuRect?.top, endRect?.top]
        .filter((value) => Number.isFinite(value))
        .map((value) => Math.round(value));
    const distinctChildTops = [...new Set(childTops)];

    const navbarWrapLikely = Boolean(
        navbar &&
        navbarRect &&
        (
            (navbar.scrollHeight - navbar.clientHeight) > 6 ||
            (navbarRect.height > 64 && distinctChildTops.length > 1)
        )
    );

    return {
        shellRect: rectSnapshot(shell),
        navbar: {
            rect: navbarRect,
            wrapLikely: navbarWrapLikely,
            styles: styleSnapshot(navbar, ['position', 'min-height', 'padding-left', 'padding-right'])
        },
        sidebar: {
            rect: sidebarRect,
            scrollHeight: sidebar ? sidebar.scrollHeight : null,
            clientHeight: sidebar ? sidebar.clientHeight : null,
            hasInternalScroll: Boolean(sidebar && sidebar.scrollHeight - sidebar.clientHeight > 12),
            styles: styleSnapshot(sidebar, ['position', 'overflow', 'max-height']),
            stickyHostStyles: styleSnapshot(sidebarStickyHost, ['position', 'top'])
        },
        main: {
            rect: rectSnapshot(main),
            styles: styleSnapshot(main, ['border-radius', 'padding-top', 'padding-left', 'padding-right'])
        },
        hero: {
            rect: rectSnapshot(hero)
        },
        ratios: {
            sidebarToMainWidth: sidebarRect && shell?.querySelector('main[role="main"]')
                ? Number((sidebarRect.width / Math.max(main?.getBoundingClientRect().width || 1, 1)).toFixed(3))
                : null,
            heroToMainHeight: hero && main
                ? Number((hero.getBoundingClientRect().height / Math.max(main.getBoundingClientRect().height, 1)).toFixed(3))
                : null
        }
    };
}

function collectStructure() {
    const main = document.querySelector('main[role="main"]');
    const sidebar = document.querySelector('.ui-menu, .menu');
    const directSections = Array.from(document.querySelectorAll('main[role="main"] > .ui-section'));
    const directHero = main?.querySelector(':scope > .ui-hero');
    const sectionTitles = [
        ...(directHero ? selectorTextList(directHero, SECTION_TITLE_SELECTORS.join(', '), 2) : []),
        ...directSections.flatMap((section) => selectorTextList(section, ':scope > .ui-container > .ui-title.size-2', 2))
    ].filter(Boolean);
    const menuLabels = selectorTextList(sidebar || document, '.ui-menu-label, .menu-label', 12);
    const menuItems = selectorTextList(sidebar || document, '.ui-menu-list > li > a > span:last-child, .menu-list > li > a > span:last-child', 24);
    const navbarItems = selectorTextList(document, '.ui-navbar-start .ui-navbar-item, .navbar-start .navbar-item', 12);
    const pageKinds = [];

    if (document.querySelector('.ui-section.page-index')) {
        pageKinds.push('page-index');
    }
    if (document.querySelector('.ui-section.page-detail')) {
        pageKinds.push('page-detail');
    }
    if (document.querySelector('.ui-section.page-form')) {
        pageKinds.push('page-form');
    }
    if (!pageKinds.length && directHero && directSections.length) {
        pageKinds.push('semantic-home');
    }

    return {
        pageKinds,
        sectionTitles,
        menuLabels,
        menuItems,
        navbarItems,
        sectionHeights: directSections.slice(0, 8).map((section, index) => ({
            index,
            height: Math.round(section.getBoundingClientRect().height)
        })),
        counts: {
            sections: directSections.length,
            cards: main ? main.querySelectorAll('.ui-card, .card').length : document.querySelectorAll('.ui-card, .card').length,
            surfaceSections: document.querySelectorAll('.ui-surface').length,
            forms: main ? main.querySelectorAll('form').length : document.querySelectorAll('form').length
        }
    };
}

function collectColorSignals(rootVars) {
    const blueFamily = [
        rootVars['--primary-cyan'],
        rootVars['--ui-link'],
        rootVars['--ui-info'],
        rootVars['--bg-surface'],
        rootVars['--bg-surface-alt']
    ].filter(Boolean);

    return {
        blueFamily,
        accentFamily: [
            rootVars['--accent-magenta'],
            rootVars['--ui-success'],
            rootVars['--ui-warning'],
            rootVars['--ui-danger']
        ].filter(Boolean)
    };
}

function collectSurfaceTables() {
    return Array.from(document.querySelectorAll('.ui-surface'))
        .map((surface, index) => {
            const title = safeText(surface.querySelector('.ui-surface-title')?.textContent);
            const body = surface.querySelector(':scope > .ui-surface-body');
            const directTable = surface.querySelector(':scope > .ui-table, :scope > .table');
            const bodyTable = body?.querySelector(':scope > .ui-table, :scope > .table') || null;
            const table = directTable || bodyTable;

            if (!table) {
                return null;
            }

            const firstHeadCell = table.querySelector('tbody th');
            const firstDataCell = table.querySelector('tbody td');

            return {
                index,
                title: title || `surface-${index + 1}`,
                hasBody: Boolean(body),
                tablePlacement: directTable ? 'direct' : 'body',
                surfaceRect: rectSnapshot(surface),
                bodyRect: rectSnapshot(body),
                tableRect: rectSnapshot(table),
                firstHeadCellRect: rectSnapshot(firstHeadCell),
                firstDataCellRect: rectSnapshot(firstDataCell),
                surfaceStyles: styleSnapshot(surface, ['padding', 'overflow', 'border-radius']),
                bodyStyles: styleSnapshot(body, ['display', 'gap', 'padding', 'margin']),
                tableStyles: styleSnapshot(table, ['margin', 'width', 'border', 'border-radius', 'box-shadow']),
                firstHeadCellStyles: styleSnapshot(firstHeadCell, ['padding-left', 'padding-right', 'padding-top', 'padding-bottom']),
                firstDataCellStyles: styleSnapshot(firstDataCell, ['padding-left', 'padding-right', 'padding-top', 'padding-bottom']),
                offsets: table && surface ? {
                    left: Math.round(table.getBoundingClientRect().left - surface.getBoundingClientRect().left),
                    right: Math.round(surface.getBoundingClientRect().right - table.getBoundingClientRect().right),
                    top: Math.round(table.getBoundingClientRect().top - surface.getBoundingClientRect().top),
                    bottom: Math.round(surface.getBoundingClientRect().bottom - table.getBoundingClientRect().bottom)
                } : null
            };
        })
        .filter(Boolean);
}

function collectUxSignals() {
    const shell = collectShell();
    const issues = [];

    if (shell.navbar.wrapLikely) {
        issues.push('Navbar likely wraps onto multiple lines on this viewport.');
    }
    if (shell.sidebar.hasInternalScroll) {
        issues.push('Sidebar currently has its own internal scroll area.');
    }
    if (shell.sidebar.stickyHostStyles?.position !== 'sticky') {
        issues.push(`Sidebar host is not sticky (position=${shell.sidebar.stickyHostStyles?.position || 'unknown'}).`);
    }
    if (shell.main.rect && shell.hero.rect) {
        const offset = shell.hero.rect.top - shell.main.rect.top;
        if (offset > 24) {
            issues.push(`Hero starts ${offset}px below the main surface top.`);
        }
    }
    if (shell.ratios.sidebarToMainWidth && shell.ratios.sidebarToMainWidth > 0.24) {
        issues.push(`Sidebar feels wide relative to main (${shell.ratios.sidebarToMainWidth}).`);
    }
    if (shell.ratios.heroToMainHeight && shell.ratios.heroToMainHeight > 0.18) {
        issues.push(`Hero occupies a large part of main (${shell.ratios.heroToMainHeight}).`);
    }

    return issues;
}

function collect() {
    const rootVars = collectRootVars();
    return {
        capturedAt: new Date().toISOString(),
        page: {
            title: document.title,
            url: window.location.href,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
                devicePixelRatio: window.devicePixelRatio || 1
            },
            scroll: {
                x: window.scrollX,
                y: window.scrollY,
                bodyHeight: document.body.scrollHeight
            },
            bodyClass: document.body.className || '',
            htmlClass: document.documentElement.className || ''
        },
        theme: {
            rootVars,
            colorSignals: collectColorSignals(rootVars)
        },
        shell: collectShell(),
        structure: collectStructure(),
        surfaceTables: collectSurfaceTables(),
        uxSignals: collectUxSignals()
    };
}

function toMarkdown(report = collect()) {
    const lines = [
        '# Arcate Design Review',
        '',
        `- Captured at: ${report.capturedAt}`,
        `- URL: ${report.page.url}`,
        `- Title: ${report.page.title}`,
        `- Viewport: ${report.page.viewport.width}x${report.page.viewport.height} @${report.page.viewport.devicePixelRatio}`,
        `- Scroll Y: ${report.page.scroll.y}`,
        '',
        '## UX Signals',
        ...(report.uxSignals.length ? report.uxSignals.map((item) => `- ${item}`) : ['- No automatic issue detected.']),
        '',
        '## Shell',
        `- Navbar wraps: ${report.shell.navbar.wrapLikely ? 'yes' : 'no'}`,
        `- Sidebar internal scroll: ${report.shell.sidebar.hasInternalScroll ? 'yes' : 'no'}`,
        `- Navbar rect: ${JSON.stringify(report.shell.navbar.rect)}`,
        `- Sidebar rect: ${JSON.stringify(report.shell.sidebar.rect)}`,
        `- Main rect: ${JSON.stringify(report.shell.main.rect)}`,
        `- Hero rect: ${JSON.stringify(report.shell.hero.rect)}`,
        `- Sidebar/Main width ratio: ${report.shell.ratios.sidebarToMainWidth ?? 'n/a'}`,
        `- Hero/Main height ratio: ${report.shell.ratios.heroToMainHeight ?? 'n/a'}`,
        '',
        '## Structure',
        `- Page kinds: ${report.structure.pageKinds.join(' | ') || 'none'}`,
        `- Counts: ${JSON.stringify(report.structure.counts)}`,
        `- Section heights: ${JSON.stringify(report.structure.sectionHeights)}`,
        '',
        '### Section Titles',
        ...(report.structure.sectionTitles.length ? report.structure.sectionTitles.map((item) => `- ${item}`) : ['- none']),
        '',
        '### Menu Labels',
        ...(report.structure.menuLabels.length ? report.structure.menuLabels.map((item) => `- ${item}`) : ['- none']),
        '',
        '### Menu Items',
        ...(report.structure.menuItems.length ? report.structure.menuItems.map((item) => `- ${item}`) : ['- none']),
        '',
        '### Navbar Items',
        ...(report.structure.navbarItems.length ? report.structure.navbarItems.map((item) => `- ${item}`) : ['- none']),
        '',
        '## Palette Signals',
        `- Blue family: ${report.theme.colorSignals.blueFamily.join(', ')}`,
        `- Accent family: ${report.theme.colorSignals.accentFamily.join(', ')}`,
        '',
        '## Theme Tokens',
        ...Object.entries(report.theme.rootVars).map(([key, value]) => `- ${key}: ${value}`)
        ,
        '',
        '## Surface Tables',
        ...(report.surfaceTables.length ? report.surfaceTables.flatMap((item) => [
            `### ${item.title}`,
            `- placement: ${item.tablePlacement}`,
            `- hasBody: ${item.hasBody ? 'yes' : 'no'}`,
            `- offsets: ${JSON.stringify(item.offsets)}`,
            `- surfaceRect: ${JSON.stringify(item.surfaceRect)}`,
            `- bodyRect: ${JSON.stringify(item.bodyRect)}`,
            `- tableRect: ${JSON.stringify(item.tableRect)}`,
            `- firstHeadCellRect: ${JSON.stringify(item.firstHeadCellRect)}`,
            `- firstDataCellRect: ${JSON.stringify(item.firstDataCellRect)}`,
            `- surfaceStyles: ${JSON.stringify(item.surfaceStyles)}`,
            `- bodyStyles: ${JSON.stringify(item.bodyStyles)}`,
            `- tableStyles: ${JSON.stringify(item.tableStyles)}`,
            `- firstHeadCellStyles: ${JSON.stringify(item.firstHeadCellStyles)}`,
            `- firstDataCellStyles: ${JSON.stringify(item.firstDataCellStyles)}`,
            ''
        ]) : ['- none'])
    ];

    return lines.join('\n');
}

function downloadText(filename, content, mimeType = 'text/plain') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function downloadJson(filename = `arcate-design-review-${Date.now()}.json`) {
    const report = collect();
    downloadText(filename, JSON.stringify(report, null, 2), 'application/json');
    return report;
}

function downloadMarkdown(filename = `arcate-design-review-${Date.now()}.md`) {
    const report = collect();
    const markdown = toMarkdown(report);
    downloadText(filename, markdown, 'text/markdown');
    return markdown;
}

async function copyMarkdown() {
    const markdown = toMarkdown();
    await navigator.clipboard.writeText(markdown);
    return markdown;
}

function shouldShowDock() {
    const params = new URLSearchParams(window.location.search);
    return params.has('design-review') || window.localStorage.getItem('arcateDesignReviewDock') === '1';
}

function createDock() {
    if (document.getElementById('arcate-design-review-dock')) {
        return;
    }

    const style = document.createElement('style');
    style.textContent = `
      #arcate-design-review-dock {
        position: fixed;
        right: 1rem;
        bottom: 1rem;
        z-index: 9999;
        display: grid;
        gap: 0.5rem;
        padding: 0.75rem;
        border-radius: 16px;
        background: rgba(8, 12, 24, 0.92);
        border: 1px solid rgba(110, 146, 223, 0.24);
        box-shadow: 0 18px 38px rgba(0, 0, 0, 0.28);
        backdrop-filter: blur(14px);
      }
      #arcate-design-review-dock button {
        min-height: 2.4rem;
        min-width: 10.5rem;
        font-size: 0.82rem;
      }
    `;
    document.head.appendChild(style);

    const dock = document.createElement('div');
    dock.id = 'arcate-design-review-dock';

    const copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.className = 'ui-button variant-outline';
    copyButton.textContent = 'Copier rapport MD';
    copyButton.addEventListener('click', async () => {
        await copyMarkdown();
        copyButton.textContent = 'Rapport copie';
        window.setTimeout(() => {
            copyButton.textContent = 'Copier rapport MD';
        }, 1200);
    });

    const downloadButton = document.createElement('button');
    downloadButton.type = 'button';
    downloadButton.className = 'ui-button tone-primary';
    downloadButton.textContent = 'Telecharger rapport';
    downloadButton.addEventListener('click', () => {
        downloadMarkdown();
    });

    dock.append(copyButton, downloadButton);
    document.body.appendChild(dock);
}

document.addEventListener('DOMContentLoaded', () => {
    if (shouldShowDock()) {
        createDock();
    }

    document.addEventListener('keydown', async (event) => {
        if (event.altKey && event.shiftKey && event.key.toLowerCase() === 'd') {
            event.preventDefault();
            await copyMarkdown();
        }
    });
});

window.arcateDesignReview = {
    collect,
    toMarkdown,
    downloadJson,
    downloadMarkdown,
    copyMarkdown
};
